import { useState, type ReactNode } from "react";
import { useNativePlatform } from "@/hooks/useNativePlatform";
import { NativeOptionSheet } from "./NativeOptionSheet";

export type NativeActionMenuItem = {
  label: string;
  onClick: () => void;
  disabled?: boolean;
};

type NativeActionMenuProps = {
  trigger: ReactNode;
  items: NativeActionMenuItem[];
  title?: string;
  nestedSheet?: boolean;
};

/** Native replacement for small action DropdownMenus (opens bottom sheet). */
export function NativeActionMenu({
  trigger,
  items,
  title = "Välj",
  nestedSheet = true,
}: NativeActionMenuProps) {
  const [open, setOpen] = useState(false);
  const enabledItems = items.filter((item) => !item.disabled);
  const options = enabledItems.map((item, index) => ({
    value: String(index),
    label: item.label,
  }));

  return (
    <>
      <div
        className="inline-flex cursor-pointer"
        role="button"
        tabIndex={0}
        onClick={(e) => {
          e.stopPropagation();
          setOpen(true);
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setOpen(true);
          }
        }}
      >
        {trigger}
      </div>
      <NativeOptionSheet
        open={open}
        onOpenChange={setOpen}
        title={title}
        value=""
        options={options}
        nestedSheet={nestedSheet}
        onSelect={(value) => {
          const item = enabledItems[Number(value)];
          if (!item) return;
          setOpen(false);
          window.setTimeout(() => item.onClick(), 50);
        }}
      />
    </>
  );
}

export function useShouldUseNativeActionMenu(): boolean {
  const { isNative } = useNativePlatform();
  return isNative;
}
